<script setup>
import { inject } from 'vue';
import { useDropZone } from '@vueuse/core';

const props = defineProps({
    visible: {
        type: Boolean,
        default: false,
    },
    targetElement: {
        type: HTMLElement,
        required: true,
    },
    noSpacing: {
        type: Boolean,
    },
});

const showDropzones = inject('showDropzones');
const emit = defineEmits(['dropped']);

useDropZone(props.targetElement, files => emit('dropped', files));
</script>

<template>
    <template v-if="targetElement">
        <Teleport
            :to="targetElement"
        >
            <div
                class="o-fileUploadDropZone"
                :class="{
                    'o-fileUploadDropZone--active': visible && showDropzones,
                    'o-fileUploadDropZone--noPadding': noSpacing,
                }"
                v-bind="$attrs"
            >
                <div class="k-c-uploadBox o-fileUploadDropZone__uploadBox">
                    <div class="o-fileUploadDropZone__textAndLink">
                        <span class="o-fileUploadDropZone__text">
                            <slot name="title"/>
                        </span>
                    </div>
                    <div class="k-c-uploadBox__info">
                        <slot name="subtitle"/>
                    </div>
                </div>
            </div>
        </Teleport>
    </template>
</template>

<style lang="scss">
    @use '~@karriereat/global-styles/scss/components/link/mixins' as link;
    @use '~@karriereat/global-styles/scss/components/upload-box' as uploadBox;
    @use '~@karriereat/global-styles/scss/settings/spacing';
    @use '~@karriereat/global-styles/scss/tools/typo';

    $iconHeightAndWidth: 22px;
    $fileSizeWidth: 60px;
    $fileExtensionWidth: 36px;
    $full-screen-overlay-z-index: 611;

    .o-fileUploadDropZone {
        position: absolute;
        top: 0;
        left: 0;
        z-index: $full-screen-overlay-z-index;
        display: grid;
        padding: spacing.$k-spacing--m;
        width: 100%;
        height: 100%;
        background-color: var(--k-white); // fallback, color-mix is not supported everywhere yet
        background-color: color-mix(in srgb, var(--k-white), transparent 20%);
        opacity: 0;
        transition: opacity 0.3s ease;
        pointer-events: none;

        &--active {
            opacity: 1;
        }

        &--noPadding {
            padding: 0;
        }

        &__uploadBox {
            display: flex;
            flex-direction: column;
            justify-content: center;
            border-color: var(--k-green-700);
            background-color: var(--k-white); // fallback, color-mix is not supported everywhere yet
            background-color: color-mix(in srgb, var(--k-white), transparent 20%);
            box-shadow: 0 0 uploadBox.$k-c-uploadBox-shadow-blur var(--k-gray-200);
        }

        &__text {
            font-weight: 500;
        }

        &__info {
            display: flex;
            flex-direction: column;
            align-items: center;
            height: 100%;
        }

        &__textAndLink {
            margin-top: spacing.$k-spacing--xs;
        }
    }
</style>
